// Builds a ready-to-edit social caption from an event item action's setup
// (raffle, auction, challenge, ...). Used by ActionPostButton to pre-fill
// MediaPost / SchedulePost so an action can be promoted straight from its
// setup screen. Plain text only: every platform composer takes it as-is and
// the user can edit it before publishing.

import { formatCurrency } from './currencyUtils';

/** "Nov 1, 2026, 5:00 PM" from a datetime-local / MySQL / date+time value. */
export const formatWhen = (value, time) => {
  if (!value) return '';
  let s = String(value).trim();
  if (time && !s.includes('T') && !s.includes(' ')) s = `${s}T${time}`;
  s = s.replace(' ', 'T');
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  // Newer ICU puts a narrow no-break space before AM/PM; keep captions plain.
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  }).replace(/[  ]/g, ' ');
};

const hashtagOf = (h) => {
  const tag = String(h || '').trim().replace(/\s+/g, '');
  if (!tag) return '';
  return tag.startsWith('#') ? tag : `#${tag}`;
};

const money = (n, currency) => {
  const v = Number(n);
  return Number.isFinite(v) && v > 0 ? formatCurrency(v, currency || undefined) : '';
};

const range = (start, end) => {
  const s = formatWhen(start);
  const e = formatWhen(end);
  if (s && e) return `${s} – ${e}`;
  if (e) return `Ends ${e}`;
  if (s) return `Starts ${s}`;
  return '';
};

/**
 * @param {object}  args
 * @param {string}  args.type    action type key (raffle, auction, challenge, ...)
 * @param {object}  args.config  the setup form's config state
 * @param {string} [args.name]   fallback name (usually the item name)
 * @param {string} [args.link]   public entry/purchase link
 * @param {Array}  [args.tiers]  ticket tiers (ticketed_event only)
 * @returns {string}
 */
export function buildActionCaption({ type, config = {}, name, link, tiers } = {}) {
  const c = config || {};
  const title = (c.name || name || '').trim();
  const lines = [];
  let cta = 'Learn more';

  switch (String(type || '').toLowerCase()) {
    case 'raffle': {
      lines.push(`🎟️ Raffle: ${title}`);
      const closes = formatWhen(c.end_date, c.end_time);
      if (closes) lines.push(`Enter before ${closes}`);
      const price = c.allow_paid_entries ? money(c.price_per_entry) : '';
      if (price) {
        lines.push(c.entry_sale_mode === 'first_free'
          ? `First entry free, extra entries ${price} each`
          : `Entries ${price} each`);
      }
      const w = Number(c.winners_per_combo);
      if (w > 1) lines.push(`${w} winners will be drawn`);
      cta = 'Enter here';
      break;
    }
    case 'auction': {
      lines.push(`🔨 Auction: ${title}`);
      const bid = money(c.starting_bid);
      if (bid) lines.push(`Starting bid: ${bid}`);
      const when = range(c.start_at, c.end_at);
      if (when) lines.push(when);
      cta = 'Bid here';
      break;
    }
    case 'challenge':
    case 'contest': {
      lines.push(`🏆 ${type === 'contest' ? 'Contest' : 'Challenge'}: ${title}`);
      if (c.description) lines.push(c.description.trim());
      if (c.entry_requirements) lines.push(`How to enter: ${c.entry_requirements.trim()}`);
      const tag = hashtagOf(c.hashtag);
      if (tag) lines.push(`Use ${tag}`);
      if (c.prize_details) lines.push(`Prize: ${c.prize_details.trim()}`);
      const when = range(c.start_at, c.end_at);
      if (when) lines.push(when);
      cta = 'Join here';
      break;
    }
    case 'ama': {
      lines.push(c.host_name ? `🎤 Ask Me Anything with ${c.host_name.trim()}` : `🎤 Ask Me Anything: ${title}`);
      if (c.host_name && title) lines.push(title);
      if (c.description) lines.push(c.description.trim());
      const when = formatWhen(c.scheduled_at);
      if (when) lines.push(c.platform ? `${when} on ${c.platform}` : when);
      cta = 'Send your questions';
      break;
    }
    case 'ugc_campaign': {
      lines.push(`📸 ${title}`);
      if (c.description) lines.push(c.description.trim());
      const tag = hashtagOf(c.hashtag);
      if (tag) lines.push(`Share yours with ${tag}`);
      const end = formatWhen(c.end_at);
      if (end) lines.push(`Open until ${end}`);
      cta = 'Submit here';
      break;
    }
    case 'themed_day': {
      lines.push(`🗓️ ${title}`);
      if (c.theme_description) lines.push(c.theme_description.trim());
      const tag = hashtagOf(c.hashtag);
      if (tag) lines.push(`Post with ${tag}`);
      const when = formatWhen(c.scheduled_at);
      if (when) lines.push(when);
      cta = 'Details';
      break;
    }
    case 'collab_takeover': {
      const kind = c.type === 'TAKEOVER' ? 'Takeover' : 'Collab';
      lines.push(c.partner_name ? `🤝 ${kind} with ${c.partner_name.trim()}` : `🤝 ${kind}: ${title}`);
      if (c.partner_name && title) lines.push(title);
      if (c.description) lines.push(c.description.trim());
      const when = formatWhen(c.scheduled_at);
      if (when) lines.push(when);
      break;
    }
    case 'poll_quiz': {
      lines.push(`📊 ${c.type === 'QUIZ' ? 'Quiz' : 'Poll'}: ${title}`);
      if (c.description) lines.push(c.description.trim());
      const q = Array.isArray(c.questions) && c.questions[0];
      const qText = q && (q.question || q.text || q.question_text);
      if (qText) lines.push(qText);
      const end = formatWhen(c.end_at);
      if (end) lines.push(`Closes ${end}`);
      cta = c.type === 'QUIZ' ? 'Take the quiz' : 'Vote here';
      break;
    }
    case 'ticketed_event': {
      lines.push(`🎫 Tickets on sale: ${title}`);
      (Array.isArray(tiers) ? tiers : [])
        .filter((t) => t && (!t.visibility || t.visibility === 'public'))
        .forEach((t) => {
          const p = Number(t.price) > 0 ? money(t.price, String(t.currency || '').toUpperCase()) : 'Free';
          lines.push(`${t.name}: ${p}`);
        });
      cta = 'Get tickets';
      break;
    }
    default:
      if (title) lines.push(title);
      if (c.description) lines.push(c.description.trim());
  }

  if (link) lines.push('', `${cta}: ${link}`);
  return lines.filter((l, i) => l || i > 0).join('\n').trim();
}

export default buildActionCaption;
